<script setup lang="ts">
import EntryAttachments from '@/core/attachments/EntryAttachments.vue';
import { EntryAttachmentType } from 'common/enum/entry.attachment.enum';

import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import { PerformanceAssessmentDocumentationMode } from '@/api/generated/api.enum';
import type { Components } from '@/api/generated/api';
import { useCompetenceAssessment } from '@/composables/competenceAssessment/useCompetenceAssessment';
import { useCompetenceObservationMutations } from '@/composables/competenceObservation/useCompetenceObservationMutations';
import { useDisplayName } from '@/composables/useDisplayName';
import { usePerformanceAssessmentStudentEntryMutations } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentStudentEntryMutations';
import CustomSeparator from '@/core/elements/CustomSeparator.vue';
import AssessmentRatingInput from '@/core/performanceAssessments/AssessmentRatingInput.vue';
import { getAssessmentDocumentationMode } from 'common/utils/performance.assessment.utils';
import UserAvatar from '@/core/UserAvatar.vue';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { immutableSort } from 'common/utils/array.utils';
import { computed, ref, toRefs, watch } from 'vue';
import PerformanceAssessmentCompetenceRatingRow from './PerformanceAssessmentCompetenceRatingRow.vue';
import PerformanceAssessmentStudentObservationEditor from './PerformanceAssessmentStudentObservationEditor.vue';

const emit = defineEmits<{
  'apply:description-to-all': [value: string];
  'select:student': [studentId: number];
  'show:performance-assessment-entry-detail': [
    { performanceAssessmentEntry: Components.Schemas.PerformanceAssessmentEntryDto },
  ];
  'update:performanceAssessmentEntry': [];
}>();

const props = withDefaults(
  defineProps<{
    gradeConfiguration: ISchoolGradeDisplayConfiguration | null;
    availableCompetenceDomains: Components.Schemas.CompetenceDomainDto[];
    applyDescriptionToAllLoading?: boolean;
    availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
    disabled?: boolean;
    highlightUnrated?: boolean;
    observations: Components.Schemas.CompetenceObservationDto[];
    performanceAssessmentEntry: Components.Schemas.PerformanceAssessmentEntryDto;
    selectableStudent?: boolean;
    showPerformanceAssessmentEntryDetailLink?: boolean;
    showApplyDescriptionToAll?: boolean;
    showAttachments?: boolean;
    showObservationEditor?: boolean;
    showStudentHeader?: boolean;
    student: Components.Schemas.StudentWithSubjectAccessDto;
  }>(),
  {
    applyDescriptionToAllLoading: false,
    disabled: false,
    highlightUnrated: false,
    selectableStudent: false,
    showPerformanceAssessmentEntryDetailLink: false,
    showApplyDescriptionToAll: false,
    showAttachments: true,
    showObservationEditor: true,
    showStudentHeader: true,
  },
);

const { observations, performanceAssessmentEntry, student } = toRefs(props);
const { currentCompetenceAssessment } = useCompetenceAssessment();
const { updateCompetenceObservation } = useCompetenceObservationMutations();
const { getStudentDisplayName } = useDisplayName();
const { updatePerformanceAssessmentStudentEntry } = usePerformanceAssessmentStudentEntryMutations();

const performanceAssessmentStudentEntry = computed<Components.Schemas.PerformanceAssessmentStudentEntryDto | null>(
  () => performanceAssessmentEntry.value.studentEntries.find((entry) => entry.studentId === student.value.id) ?? null,
);
const description = ref(performanceAssessmentStudentEntry.value?.description ?? '');

// Bulk saves can update annotations while this editor is retained in the overview.
// Reload the draft on return to the individual view without interrupting pending saves.
watch(
  () => props.showObservationEditor,
  (show) => {
    if (show) description.value = performanceAssessmentStudentEntry.value?.description ?? '';
  },
);

const observationsSorted = computed<Components.Schemas.CompetenceObservationDto[]>(() =>
  immutableSort(observations.value, (a, b) => a.id - b.id),
);

const performanceAssessment = computed<Components.Schemas.BasePerformanceAssessmentDto | null>(
  () => performanceAssessmentEntry.value.performanceAssessment ?? null,
);
const hasOverallRating = computed<boolean>(
  () =>
    !!performanceAssessment.value &&
    getAssessmentDocumentationMode(performanceAssessment.value) !==
      PerformanceAssessmentDocumentationMode.COMPETENCE_ONLY,
);
const isSchoolGrade = computed(
  () =>
    getAssessmentDocumentationMode(performanceAssessment.value) === PerformanceAssessmentDocumentationMode.SCHOOL_GRADE,
);

const shouldShowStudentHeader = computed<boolean>(
  () => hasOverallRating.value || props.showStudentHeader || props.showPerformanceAssessmentEntryDetailLink,
);

const showRatingPanel = computed<boolean>(
  () => shouldShowStudentHeader.value || hasOverallRating.value || observationsSorted.value.length > 0,
);

const saveStudentRating = computed(() => {
  // Queued saves must keep their original target when the keyed rating input is replaced.
  const studentEntryId = performanceAssessmentStudentEntry.value?.id;
  const schoolClassId = performanceAssessmentEntry.value.schoolClassId;
  const performanceAssessmentEntryId = performanceAssessmentEntry.value.id;
  const schoolGrade = isSchoolGrade.value;
  return async (value: number | null): Promise<void> => {
    assertNotNullOrUndefined(studentEntryId);
    await updatePerformanceAssessmentStudentEntry({
      schoolClassId,
      performanceAssessmentEntryId,
      studentEntryId,
      dto: schoolGrade ? { gradeValue: value } : { performanceLevelV2Id: value },
    });
    emit('update:performanceAssessmentEntry');
  };
});

const handleChangeStudentEntryDescription = async (value: string): Promise<void> => {
  const studentEntry = performanceAssessmentStudentEntry.value;
  assertNotNullOrUndefined(studentEntry);

  description.value = value;
  if (value === (studentEntry.description ?? '')) return;

  await updatePerformanceAssessmentStudentEntry({
    schoolClassId: performanceAssessmentEntry.value.schoolClassId,
    performanceAssessmentEntryId: performanceAssessmentEntry.value.id,
    studentEntryId: studentEntry.id,
    dto: { description: value },
  });
  emit('update:performanceAssessmentEntry');
};

const handleUpdateObservation = async (value: Components.Schemas.CompetenceObservationDto): Promise<void> => {
  await updateCompetenceObservation({
    schoolClassId: performanceAssessmentEntry.value.schoolClassId,
    performanceAssessmentEntryId: performanceAssessmentEntry.value.id,
    observationId: value.id,
    dto: { performanceLevelV2Id: value.performanceLevelV2Id, description: value.description },
  });
  emit('update:performanceAssessmentEntry');
};

const showPerformanceAssessmentEntryDetail = (): void => {
  emit('show:performance-assessment-entry-detail', { performanceAssessmentEntry: performanceAssessmentEntry.value });
};
</script>

<template>
  <li class="flex flex-col gap-md">
    <div v-if="showRatingPanel" class="overflow-hidden rounded-2xl bg-surface-secondary py-sm">
      <div v-if="shouldShowStudentHeader" class="flex items-center justify-between gap-md px-md py-sm">
        <div class="flex min-w-0 flex-1 items-center gap-md">
          <button
            v-if="selectableStudent"
            type="button"
            class="shrink-0 cursor-pointer rounded-full outline-inner"
            :aria-label="getStudentDisplayName(student)"
            @click="emit('select:student', student.id)"
          >
            <UserAvatar :user="student" class="!size-button" />
          </button>
          <UserAvatar v-else :user="student" class="!size-button shrink-0" />
          <div class="flex min-w-0 flex-1 flex-col items-start">
            <div class="flex min-w-0 max-w-full items-center gap-sm">
              <button
                v-if="selectableStudent"
                type="button"
                class="min-w-0 cursor-pointer truncate text-left text-body font-semibold leading-body text-primary outline-inner hover:underline active:underline focus-visible:underline"
                :title="getStudentDisplayName(student)"
                @click="emit('select:student', student.id)"
              >
                {{ getStudentDisplayName(student) }}
              </button>
              <span
                v-else
                class="truncate text-body font-semibold leading-body text-primary"
                :title="getStudentDisplayName(student)"
              >
                {{ getStudentDisplayName(student) }}
              </span>
              <span
                v-if="highlightUnrated"
                aria-hidden="true"
                class="size-icon-xxs shrink-0 rounded-full bg-surface-warning"
              />
            </div>
            <button
              v-if="showPerformanceAssessmentEntryDetailLink"
              type="button"
              class="max-w-full truncate text-left text-link-primary text-secondary"
              @click="showPerformanceAssessmentEntryDetail"
            >
              {{ performanceAssessmentEntry.label }}
            </button>
          </div>
        </div>
        <AssessmentRatingInput
          v-if="hasOverallRating && performanceAssessment && performanceAssessmentStudentEntry"
          :key="performanceAssessmentStudentEntry.id"
          :grade-configuration="gradeConfiguration"
          class="ml-auto max-w-full shrink-0 max-md:max-w-2/5"
          :accessible-label="getStudentDisplayName(student)"
          :disabled="disabled"
          :model-value="
            isSchoolGrade
              ? performanceAssessmentStudentEntry.gradeValue
              : performanceAssessmentStudentEntry.performanceLevelV2Id
          "
          :assessment="performanceAssessment"
          :save="saveStudentRating"
        />
      </div>

      <div v-if="shouldShowStudentHeader && observationsSorted.length" class="px-md py-sm">
        <CustomSeparator />
      </div>

      <ul v-if="currentCompetenceAssessment && observationsSorted.length">
        <template v-for="(observation, index) in observationsSorted" :key="observation.id">
          <PerformanceAssessmentCompetenceRatingRow
            :assessment="currentCompetenceAssessment"
            :available-competence-domains="availableCompetenceDomains"
            :available-school-subjects="availableSchoolSubjects"
            :disabled="disabled"
            :observation="observation"
            @update:observation="handleUpdateObservation"
          />
          <div v-if="index < observationsSorted.length - 1" class="px-md py-sm">
            <CustomSeparator />
          </div>
        </template>
      </ul>
    </div>

    <PerformanceAssessmentStudentObservationEditor
      v-if="showObservationEditor && performanceAssessmentStudentEntry"
      :apply-to-all-loading="applyDescriptionToAllLoading"
      :disabled="disabled"
      :name="`student-entry-observation-${student.id}`"
      :show-apply-to-all="showApplyDescriptionToAll"
      :value="description"
      @update:value="description = $event"
      @commit="handleChangeStudentEntryDescription"
      @apply-to-all="emit('apply:description-to-all', $event)"
    />
    <EntryAttachments
      v-if="showAttachments && performanceAssessmentStudentEntry"
      :key="performanceAssessmentStudentEntry.id"
      :entry-type="EntryAttachmentType.PerformanceAssessmentStudent"
      :entry-id="performanceAssessmentStudentEntry.id"
      :readonly="disabled"
    />
  </li>
</template>
